import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PawPrint, ArrowRight, SealCheck, Eye, Plus, X, ShoppingBag, HandTap } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/e8627588331403b122f1c64001d47d610bbc1a4056cab53899d78127ab0b49a9.jpeg";

// Hotspot positions over the trench hero look (human center-left, dog lower-right).
const HOTSPOTS = [
  { key: "human", role: "human", top: "40%", left: "37%" },
  { key: "dog", role: "dog", top: "63%", left: "60%" },
];

export default function HouseOfWigglez() {
  const [fits, setFits] = useState([]);
  const [openSpot, setOpenSpot] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { category: "matching" } }),
      api.get("/products", { params: { category: "dog-gear" } }),
    ]).then(([m, d]) => {
      const humans = m.data.filter((p) => p.collection === "signature");
      const dogs = d.data.filter((p) => p.collection === "signature");
      const order = ["sig-monogram", "sig-knit", "sig-offset", "sig-boucle", "sig-trench"];
      const built = humans
        .map((h) => ({ human: h, dog: dogs.find((x) => x.pack_key === h.pack_key) }))
        .sort((a, b) => order.indexOf(a.human.pack_key) - order.indexOf(b.human.pack_key));
      setFits(built);
    }).catch(() => {});
  }, []);

  const heroFit = fits.find((f) => f.human.pack_key === "sig-trench") || fits[0];

  return (
    <div className="bg-[#050505]" data-testid="house-page">
      {/* Editorial hero — Shop the Look */}
      <section className="relative h-[86vh] min-h-[520px] overflow-hidden">
        <img src={heroFit?.human?.images?.[0] || HERO} alt="The Signature Line" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-background/40" />

        <div className="absolute top-24 left-5 sm:left-8 z-20 overline px-3 py-1.5 rounded-full bg-black/55 backdrop-blur border border-white/15 inline-flex items-center gap-1.5 text-primary" data-testid="house-shop-the-look">
          <HandTap weight="fill" size={13} /> Shop the Look · tap the pieces
        </div>

        {heroFit && HOTSPOTS.map((spot) => {
          const product = spot.role === "dog" ? heroFit.dog : heroFit.human;
          if (!product) return null;
          const open = openSpot === spot.key;
          return (
            <div key={spot.key} className="absolute z-30" style={{ top: spot.top, left: spot.left }}>
              <button onClick={() => setOpenSpot(open ? null : spot.key)} aria-label={`Shop ${product.name}`}
                className="relative grid place-items-center h-8 w-8 rounded-full bg-white text-black shadow-lg hover:scale-110 transition-transform" data-testid={`house-hotspot-${spot.key}`}>
                {!open && <span className="absolute inline-flex h-full w-full rounded-full bg-primary/70 animate-ping" />}
                <Plus weight="bold" size={16} className="relative" />
              </button>
              <AnimatePresence>
                {open && (
                  <motion.div initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-60 rounded-xl border border-white/15 bg-[#0b0b0b]/95 backdrop-blur-xl p-3 shadow-2xl" data-testid={`house-hotspot-card-${spot.key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline text-secondary text-[10px]">{spot.role === "dog" ? "Dog gear" : "The fit"}</span>
                      <button onClick={() => setOpenSpot(null)} className="text-muted-foreground hover:text-white" aria-label="Close"><X size={14} /></button>
                    </div>
                    <div className="flex gap-3">
                      <img src={(product.images || [])[0]} alt={product.name} className="h-16 w-16 rounded-lg object-cover shrink-0" />
                      <div className="min-w-0">
                        <p className="font-display font-black uppercase text-sm leading-tight">{product.name}</p>
                        <p className="text-sm text-muted-foreground">${product.price.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => { addItem(product, 1, {}); toast.success(`${product.name} added 🐾`); setOpenSpot(null); }}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground px-3 py-2 rounded-[9px] text-sm font-modern hover:bg-primary/90 transition-colors" data-testid={`house-hotspot-add-${spot.key}`}>
                        <ShoppingBag weight="fill" size={14} /> Add
                      </button>
                      <Link to={`/product/${product.id}`} className="inline-flex items-center justify-center border border-white/20 text-white px-3 py-2 rounded-[9px] text-sm hover:border-white/40 transition-colors">View</Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-16">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="overline mb-4 tracking-[0.4em]" style={{ color: "#fbbf24" }}>House of Wigglez · The Signature Line</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display font-black uppercase tracking-tighter leading-[0.88] text-5xl sm:text-7xl lg:text-8xl max-w-4xl">
            The details<br /><span className="gold-text">only you know.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-6 text-muted-foreground max-w-xl text-base sm:text-lg leading-relaxed">
            Luxury fits cut for the street and the room. Clean on the outside — hidden hypercolor camo, offset colorways and a gold heart within. And the dog always matches.
          </motion.p>
        </div>
      </section>

      {/* Editorial fits — alternating, slow reveals */}
      {fits.map(({ human, dog }, i) => {
        const flip = i % 2 === 1;
        const hero = (human.images || [])[0];
        const detail = (human.images || [])[1] || hero;
        return (
          <section key={human.id} className={`relative py-20 sm:py-28 ${i % 2 ? "bg-[#080808]" : "bg-[#050505]"}`} data-testid={`house-fit-${human.id}`}>
            <div className={`max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flip ? "lg:[direction:rtl]" : ""}`}>
              {/* Imagery */}
              <div className="relative lg:[direction:ltr]">
                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
                  className="rounded-2xl overflow-hidden border border-white/10">
                  <img src={hero} alt={human.name} className="w-full aspect-[3/4] object-cover" />
                </motion.div>
                {/* Hidden-detail macro inset */}
                <motion.div initial={{ opacity: 0, scale: 0.9, y: 30 }} whileInView={{ opacity: 1, scale: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: 0.25 }}
                  className="absolute -bottom-8 w-40 sm:w-52 rounded-xl overflow-hidden border-2 border-amber-400/40 shadow-2xl"
                  style={{ [flip ? "left" : "right"]: "-1rem" }}>
                  <img src={detail} alt={`${human.name} hidden detail`} className="w-full aspect-square object-cover" />
                  <div className="absolute top-2 left-2 overline text-[9px] px-2 py-0.5 rounded-full text-black font-semibold inline-flex items-center gap-1" style={{ background: "#fbbf24" }}>
                    <Eye weight="fill" size={9} /> Hidden detail
                  </div>
                </motion.div>
              </div>

              {/* Copy */}
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: 0.15 }}
                className="lg:[direction:ltr]">
                <p className="overline text-muted-foreground mb-2 inline-flex items-center gap-1.5"><SealCheck weight="fill" size={12} className="text-accent" /> Signed · {human.pack_label}</p>
                <h2 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl">{human.name}</h2>
                <p className="text-sm font-bold uppercase tracking-[0.2em] mt-3" style={{ color: "#fbbf24" }}>{human.slogan}</p>
                <p className="text-muted-foreground leading-relaxed mt-4 max-w-lg">{human.description}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <span className="text-2xl font-modern">${human.price.toLocaleString()}</span>
                  <Link to={`/product/${human.id}`} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-[10px] font-modern hover:bg-primary/90 transition-colors" data-testid={`house-shop-${human.id}`}>
                    <PawPrint weight="fill" size={16} /> Shop the fit <ArrowRight size={15} />
                  </Link>
                </div>
                {dog && (
                  <Link to={`/product/${dog.id}`} className="mt-4 inline-flex items-center gap-3 rounded-xl border border-secondary/25 bg-secondary/5 px-4 py-3 hover:border-secondary/50 transition-colors group" data-testid={`house-dog-${dog.id}`}>
                    <img src={(dog.images || [])[0]} alt={dog.name} className="h-11 w-11 rounded-lg object-cover" />
                    <span className="text-sm">
                      <span className="overline text-secondary block text-[10px]">The dog matches</span>
                      <span className="text-white">{dog.name} · ${dog.price.toLocaleString()}</span>
                    </span>
                    <ArrowRight size={15} className="text-secondary group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}

      {/* Close */}
      <section className="py-20 text-center border-t border-white/10">
        <p className="overline mb-3" style={{ color: "#fbbf24" }}>House of Wigglez</p>
        <h2 className="font-display font-black uppercase text-3xl sm:text-5xl max-w-2xl mx-auto leading-[0.95]">Wear the mission. <span className="gold-text">Match the pack.</span></h2>
        <Link to="/shop" className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-[12px] font-display font-black uppercase hover:bg-primary/90 transition-colors" data-testid="house-shop-all">
          Shop the collection <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
